<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card elevation="2">
          <v-toolbar color="primary" elevation="1">
            <v-spacer />Dashboard Individual dos TBLs<v-spacer />
          </v-toolbar>
          <v-card-text>
            <v-row class="mb-3">
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedTurmaCodigo"
                  density="comfortable"
                  item-title="label"
                  item-value="codigo"
                  label="Turma"
                  prepend-inner-icon="mdi-account-group"
                  variant="outlined"
                  :items="turmas"
                  :loading="loadingTurmas"
                  @update:model-value="loadDashboard"
                />
              </v-col>
            </v-row>

            <v-alert v-if="loading" type="info" variant="tonal">
              Carregando notas dos TBLs da turma selecionada...
            </v-alert>
            <v-alert v-else-if="selectedTurmaCodigo && !students.length" type="warning" variant="tonal">
              Nenhum aluno com TBL realizado foi encontrado nesta turma.
            </v-alert>
            <v-alert v-else-if="!selectedTurmaCodigo" type="warning" variant="tonal">
              Selecione uma turma para carregar o dashboard.
            </v-alert>

            <v-row v-else-if="students.length">
              <v-col cols="12" md="4" lg="3">
                <v-text-field
                  v-model="studentFilter"
                  clearable
                  density="comfortable"
                  label="Buscar aluno"
                  prepend-inner-icon="mdi-magnify"
                  variant="outlined"
                />
                <div class="student-list">
                  <v-chip-group v-model="selectedStudentId" direction="vertical" class="student-chip-group">
                    <v-chip
                      v-for="student in filteredStudents"
                      :key="student.id"
                      :value="student.id"
                      class="student-chip"
                      :class="{ 'student-chip--selected': selectedStudentId === student.id }"
                      variant="tonal"
                      @click="selectStudent(student.id)"
                    >
                      <div class="student-chip-content">
                        <strong>{{ student.nome }}</strong>
                        <span>RA {{ student.ra || "-" }}</span>
                        <span>{{ student.tests_count }} TBLs | Média {{ formatGrade(student.avg_final) }}</span>
                      </div>
                    </v-chip>
                  </v-chip-group>
                </div>
              </v-col>

              <v-col cols="12" md="8" lg="9">
                <v-row>
                  <v-col cols="12" md="5">
                    <v-select
                      v-model="selectedDisciplina"
                      clearable
                      density="comfortable"
                      label="Disciplina"
                      prepend-inner-icon="mdi-book-open-page-variant"
                      variant="outlined"
                      :items="disciplineOptions"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-card class="summary-card" elevation="1">
                      <span>Aluno</span>
                      <strong>{{ selectedStudent?.nome || "-" }}</strong>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-card class="summary-card" elevation="1">
                      <span>TBLs</span>
                      <strong>{{ selectedRecords.length }}</strong>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-card class="summary-card" elevation="1">
                      <span>Média Final</span>
                      <strong>{{ formatGrade(averageFinal) }}</strong>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-card class="summary-card" elevation="1">
                      <span>Maior Nota</span>
                      <strong>{{ formatGrade(bestFinal) }}</strong>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row class="mt-2">
                  <v-col cols="12">
                    <v-card elevation="2">
                      <v-toolbar color="primary" elevation="1">
                        <v-spacer />Evolução por TBL — AI + AE<v-spacer />
                      </v-toolbar>
                      <v-card-text style="height: 340px;">
                        <Bar :data="chartDataStacked" :options="chartOptionsStacked" />
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row class="mt-2">
                  <v-col cols="12">
                    <v-card elevation="2">
                      <v-toolbar color="secondary" elevation="1">
                        <v-spacer />Nota Final (%)<v-spacer />
                      </v-toolbar>
                      <v-card-text style="height: 300px;">
                        <Line :data="chartDataPercent" :options="chartOptionsPercent" />
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row class="mt-2">
                  <v-col
                    v-for="record in selectedRecords"
                    :key="`gauge-${record.test_id}`"
                    cols="12"
                    md="6"
                    xl="4"
                  >
                    <v-card elevation="2" height="100%">
                      <v-toolbar color="primary-darken-1" elevation="1">
                        <v-spacer />{{ record.test_name }}<v-spacer />
                      </v-toolbar>
                      <v-card-text>
                        <div class="gauge-wrap">
                          <Doughnut :data="buildGaugeData(record)" :options="chartOptionsGauge" />
                        </div>
                        <div class="gauge-caption">
                          <strong>{{ formatGrade(record.total_final) }}</strong>
                          <span>de {{ formatGrade(record.nota_maxima) }} pontos | {{ formatGrade(record.percent) }}%</span>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-row class="mt-2">
                  <v-col cols="12">
                    <v-card elevation="2">
                      <v-toolbar color="primary-darken-1" elevation="1">
                        <v-spacer />Notas dos TBLs realizados<v-spacer />
                      </v-toolbar>
                      <v-card-text>
                        <v-data-table
                          class="table elevation-1"
                          density="comfortable"
                          items-per-page-text="Itens por página"
                          page-text="{0}-{1} de {2}"
                          :headers="headers"
                          :items="selectedRecords"
                          :items-per-page="10"
                        >
                          <template v-slot:item.datetime="{ item }">
                            {{ formatDate(item.columns.datetime) }}
                          </template>
                          <template v-slot:item.total_ai="{ item }">
                            {{ formatGrade(item.columns.total_ai) }}
                          </template>
                          <template v-slot:item.total_ae="{ item }">
                            {{ formatGrade(item.columns.total_ae) }}
                          </template>
                          <template v-slot:item.total_final="{ item }">
                            {{ formatGrade(item.columns.total_final) }}
                          </template>
                          <template v-slot:item.percent="{ item }">
                            {{ formatGrade(item.columns.percent) }}%
                          </template>
                        </v-data-table>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "vue-chartjs";
import { VDataTable } from "vuetify/labs/VDataTable";
import TestDataService from "@/assets/js/services/TestDataService";
import UserDataService from "@/assets/js/services/UserDataService";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
);

export default {
  name: "TBLDashboardIndividual",
  components: { Bar, Doughnut, Line, VDataTable },

  data: () => ({
    loading: true,
    loadingTurmas: true,
    studentFilter: "",
    selectedTurmaCodigo: "",
    turmas: [],
    selectedStudentId: null,
    selectedDisciplina: "",
    students: [],
    records: [],
    headers: [
      { title: "TBL", key: "test_name", align: "start" },
      { title: "Disciplina", key: "disciplina", align: "start" },
      { title: "Professor", key: "professor_nome", align: "start" },
      { title: "Data", key: "datetime", align: "center" },
      { title: "AI", key: "total_ai", align: "center" },
      { title: "AE", key: "total_ae", align: "center" },
      { title: "Final", key: "total_final", align: "center" },
      { title: "%", key: "percent", align: "center" },
    ],
    chartOptionsStacked: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "top" } },
      scales: {
        x: { stacked: true },
        y: { stacked: true, beginAtZero: true },
      },
    },
    chartOptionsPercent: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        y: { beginAtZero: true, max: 100 },
      },
    },
    chartOptionsGauge: {
      responsive: true,
      maintainAspectRatio: false,
      circumference: 180,
      rotation: -90,
      cutout: "70%",
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
    },
  }),

  computed: {
    filteredStudents() {
      const term = String(this.studentFilter || "").trim().toLocaleLowerCase("pt-BR");
      if (!term) return this.students;
      return this.students.filter((student) =>
        String(student.nome || "").toLocaleLowerCase("pt-BR").includes(term)
      );
    },

    selectedStudent() {
      return this.students.find((student) => student.id === this.selectedStudentId) || null;
    },

    selectedRecords() {
      return this.records
        .filter((record) => record.student_id === this.selectedStudentId)
        .filter((record) => !this.selectedDisciplina || record.disciplina === this.selectedDisciplina)
        .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
    },

    disciplineOptions() {
      return Array.from(new Set(
        this.records
          .filter((record) => record.student_id === this.selectedStudentId)
          .map((record) => record.disciplina)
          .filter(Boolean)
      )).sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }));
    },

    averageFinal() {
      if (!this.selectedRecords.length) return 0;
      const total = this.selectedRecords.reduce((sum, record) => sum + Number(record.total_final || 0), 0);
      return total / this.selectedRecords.length;
    },

    bestFinal() {
      if (!this.selectedRecords.length) return 0;
      return Math.max(...this.selectedRecords.map((record) => Number(record.total_final || 0)));
    },

    chartDataStacked() {
      return {
        labels: this.selectedRecords.map((record) => record.test_name),
        datasets: [
          {
            label: "Atividade Individual (AI)",
            backgroundColor: "#3C6E8F",
            data: this.selectedRecords.map((record) => record.total_ai),
          },
          {
            label: "Atividade em Equipe (AE)",
            backgroundColor: "#B88645",
            data: this.selectedRecords.map((record) => record.total_ae),
          },
        ],
      };
    },

    chartDataPercent() {
      return {
        labels: this.selectedRecords.map((record) => record.test_name),
        datasets: [
          {
            label: "Nota Final (%)",
            borderColor: "#2E7D32",
            backgroundColor: "rgba(46,125,50,0.18)",
            tension: 0.25,
            fill: true,
            data: this.selectedRecords.map((record) => record.percent),
          },
        ],
      };
    },
  },

  mounted() {
    this.loadTurmas();
  },

  methods: {
    async loadTurmas() {
      this.loading = false;
      this.loadingTurmas = true;
      try {
        const response = await UserDataService.listTurmas();
        this.turmas = this.normalizeTurmas(response.data || []);
        if (!this.turmas.some((turma) => turma.codigo === this.selectedTurmaCodigo)) {
          this.selectedTurmaCodigo = this.turmas[0]?.codigo || "";
        }
        if (this.selectedTurmaCodigo) {
          await this.loadDashboard();
        }
      } catch (error) {
        console.log(error);
        this.turmas = [];
        this.selectedTurmaCodigo = "";
      } finally {
        this.loadingTurmas = false;
      }
    },

    async loadDashboard() {
      if (!this.selectedTurmaCodigo) {
        this.students = [];
        this.records = [];
        this.selectedStudentId = null;
        return;
      }

      this.loading = true;
      try {
        const response = await TestDataService.studentDashboard({
          turma_codigo: this.selectedTurmaCodigo,
        });
        this.turmas = this.normalizeTurmas(response.data.turmas || this.turmas);
        this.students = (response.data.students || []).sort((a, b) =>
          String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" })
        );
        this.records = response.data.records || [];
        this.selectedStudentId = this.students[0]?.id || null;
        this.selectedDisciplina = "";
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    selectStudent(studentId) {
      this.selectedStudentId = studentId;
      this.selectedDisciplina = "";
    },

    normalizeTurmas(turmas) {
      return (Array.isArray(turmas) ? turmas : [])
        .map((turma) => ({
          id: turma.id || turma.codigo,
          codigo: String(turma.codigo || turma.nome || "").toUpperCase(),
          nome: turma.nome || `Turma ${turma.codigo}`,
          label: String(turma.codigo || turma.nome || "").toUpperCase(),
        }))
        .filter((turma) => turma.codigo)
        .sort((a, b) => a.codigo.localeCompare(b.codigo, "pt-BR", { sensitivity: "base" }));
    },

    formatDate(value) {
      if (!value) return "-";
      return new Intl.DateTimeFormat("pt-BR", {
        timeZone: "America/Sao_Paulo",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(value));
    },

    formatGrade(value) {
      const number = Number(value || 0);
      return number.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getGaugeColor(percent) {
      const value = Number(percent || 0);
      if (value < 30) return "#B00020";
      if (value < 60) return "#FBC02D";
      return "#2E7D32";
    },

    buildGaugeData(record) {
      const percent = Math.max(0, Math.min(Number(record.percent || 0), 100));
      return {
        labels: ["Nota", "Restante"],
        datasets: [
          {
            data: [percent, Math.max(0, 100 - percent)],
            backgroundColor: [this.getGaugeColor(percent), "#E5E7EB"],
            borderWidth: 0,
          },
        ],
      };
    },
  },
};
</script>

<style scoped>
.student-list {
  max-height: 392px;
  overflow-y: auto;
  padding-right: 8px;
}

.student-chip-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.student-chip {
  width: 100%;
  min-height: 44px;
  max-width: none;
  white-space: normal;
  text-align: left;
}

.student-chip-content {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.student-chip-content span {
  font-size: 12px;
  opacity: 0.75;
}

.student-chip--selected {
  background-color: rgba(60, 110, 143, 0.16) !important;
}

.summary-card {
  display: flex;
  min-height: 84px;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 14px 16px;
}

.summary-card span {
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.summary-card strong {
  color: #16324a;
  font-size: 20px;
}

.gauge-wrap {
  height: 190px;
  position: relative;
}

.gauge-caption {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: -22px;
  text-align: center;
}

.gauge-caption strong {
  color: #16324a;
  font-size: 24px;
}

.gauge-caption span {
  color: #5f6b7a;
  font-size: 13px;
}
</style>
